import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { useSelector } from "react-redux";
import imageCompression from "browser-image-compression";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";

import CardSection from "../../../components/CardSection/CardSection";
import { selectActiveUid } from "../../Auth/authSlice";
import { functions, db } from "../../../config/firebaseConfig";
import { getKinshasaDateIso } from "../../../utils/dateUtils";

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const COMPRESSION_OPTIONS = {
  maxSizeMB: 0.8,
  maxWidthOrHeight: 1280,
  useWebWorker: true,
  fileType: "image/jpeg",
  initialQuality: 0.75,
};

function safeFileName(name) {
  return String(name || "photo").replace(/[^a-zA-Z0-9._-]/g, "_");
}

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",").pop() : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function buildDescription(type, { pompe, dateVente, paiement, montant, commentaire }) {
  if (type === "POMPE") return `POMPE : ${pompe.trim()}`;
  if (type === "BV") {
    const [y, m, d] = dateVente.split("-");
    return `BV | ${d}/${m}/${y} | ${paiement} | ${montant.trim()}`;
  }
  return `NOTE : ${commentaire.trim()}`;
}

// "OIL_SHOP" → ["OIL_CDF", "OIL_USD", "SHOP_CDF", "SHOP_USD"]
function parsePaiementOptions(verString) {
  if (!verString) return [];
  const tokens = verString.split("_");
  const result = [];
  for (const token of tokens) {
    result.push(`${token}_CDF`);
    result.push(`${token}_USD`);
  }
  return result;
}

export default function PhotoNote() {
  const activeUid = useSelector(selectActiveUid);
  const [cardOpen, setCardOpen] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [type, setType] = useState("POMPE");

  // POMPE
  const [pompe, setPompe] = useState("");

  // BV
  const [dateVente, setDateVente] = useState(() => getKinshasaDateIso());
  const [verString, setVerString] = useState("");
  const [paiement, setPaiement] = useState("");
  const [montant, setMontant] = useState("");

  // AUTRE
  const [commentaire, setCommentaire] = useState("");

  const paiementOptions = useMemo(
    () => parsePaiementOptions(verString),
    [verString],
  );

  useEffect(() => {
    if (!activeUid) {
      setVerString("");
      return undefined;
    }
    let canceled = false;
    (async () => {
      try {
        const userDocSnap = await getDoc(doc(db, "users", activeUid));
        if (!canceled && userDocSnap.exists()) {
          setVerString(userDocSnap.data().ver || "");
        }
      } catch (e) {
        console.error("load ver failed:", e);
      }
    })();
    return () => {
      canceled = true;
    };
  }, [activeUid]);

  // Keep the selected payment type valid as the station's option list loads/changes.
  useEffect(() => {
    setPaiement((prev) =>
      paiementOptions.includes(prev) ? prev : paiementOptions[0] || "",
    );
  }, [paiementOptions]);

  useEffect(() => {
    if (!photo) {
      setPreviewUrl("");
      return undefined;
    }
    const nextPreviewUrl = URL.createObjectURL(photo);
    setPreviewUrl(nextPreviewUrl);
    return () => URL.revokeObjectURL(nextPreviewUrl);
  }, [photo]);

  const handleTypeChange = (_, nextType) => {
    if (!nextType) return;
    setType(nextType);
    setPompe("");
    setMontant("");
    setCommentaire("");
    setError("");
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    setError("");
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Selectionnez une image.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError("Image trop grande. Maximum 20 MB.");
      return;
    }
    setPhoto(file);
  };

  const handleClearPhoto = () => {
    setPhoto(null);
    setError("");
  };

  const fieldsComplete =
    type === "POMPE" ? Boolean(pompe.trim()) :
    type === "BV"    ? Boolean(dateVente && paiement && montant.trim()) :
                       Boolean(commentaire.trim());

  const canSend = Boolean(activeUid && photo && fieldsComplete && !loading);

  const handleSend = async () => {
    if (!canSend) return;
    setLoading(true);
    setError("");
    try {
      const description = buildDescription(type, { pompe, dateVente, paiement, montant, commentaire });
      const compressedPhoto = await imageCompression(photo, COMPRESSION_OPTIONS);
      const imageBase64 = await readAsBase64(compressedPhoto);
      const sendPhotoNote = httpsCallable(functions, "photoNoteToSlack");

      await sendPhotoNote({
        uid: activeUid,
        description,
        imageBase64,
        contentType: compressedPhoto.type || "image/jpeg",
        fileName: safeFileName(photo.name.replace(/\.[^.]+$/, "") + ".jpg"),
        ...(type === "BV"
          ? { bvItem: paiement, bvDateVente: dateVente, bvMontant: montant }
          : {}),
      });

      setPhoto(null);
      setPompe("");
      setMontant("");
      setCommentaire("");
      setCardOpen(false);
    } catch (err) {
      console.error("Error uploading photo note:", err);
      setError("Envoi impossible. Reessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CardSection
      title="PHOTO & NOTE"
      showDefaultControls={false}
      open={cardOpen}
      setOpen={setCardOpen}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>

        {/* Sélecteur de type */}
        <ToggleButtonGroup
          value={type}
          exclusive
          onChange={handleTypeChange}
          fullWidth
          size="small"
          disabled={loading}
        >
          <ToggleButton value="POMPE">Pompe</ToggleButton>
          <ToggleButton value="BV">BV</ToggleButton>
          <ToggleButton value="AUTRE">Autre</ToggleButton>
        </ToggleButtonGroup>

        {/* Champs POMPE */}
        {type === "POMPE" && (
          <TextField
            label="Pompes"
            placeholder="P1S1G2"
            value={pompe}
            onChange={(e) => setPompe(e.target.value.toUpperCase().replace(/\s+/g, ""))}
            disabled={loading}
            size="small"
            fullWidth
          />
        )}

        {/* Champs BV */}
        {type === "BV" && (
          <>
            <TextField
              label="Date de vente"
              type="date"
              value={dateVente}
              onChange={(e) => setDateVente(e.target.value)}
              disabled={loading}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Paiement"
              value={paiement}
              onChange={(e) => setPaiement(e.target.value)}
              disabled={loading || paiementOptions.length === 0}
              size="small"
              fullWidth
            >
              {paiementOptions.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
            <NumericFormat
              customInput={TextField}
              label="Montant"
              value={montant}
              onValueChange={({ formattedValue }) => setMontant(formattedValue)}
              thousandSeparator=" "
              decimalSeparator=","
              decimalScale={2}
              allowedDecimalSeparators={[",", "."]}
              inputMode="decimal"
              disabled={loading}
              size="small"
              fullWidth
            />
          </>
        )}

        {/* Champs AUTRE */}
        {type === "AUTRE" && (
          <TextField
            label="Commentaire"
            value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
            disabled={loading}
            size="small"
            fullWidth
            multiline
            rows={3}
          />
        )}

        {/* Bouton photo */}
        <Button
          component="label"
          variant="outlined"
          startIcon={<AddPhotoAlternateIcon />}
          disabled={loading}
          fullWidth
        >
          Photo
          <input
            hidden
            accept="image/*"
            type="file"
            onChange={handlePhotoChange}
          />
        </Button>

        {/* Aperçu */}
        {previewUrl && (
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              component="img"
              src={previewUrl}
              alt="Apercu"
              sx={{
                display: "block",
                width: "100%",
                maxHeight: 260,
                objectFit: "contain",
                bgcolor: "grey.100",
              }}
            />
            <Button
              size="small"
              variant="contained"
              color="inherit"
              startIcon={<CloseIcon />}
              onClick={handleClearPhoto}
              disabled={loading}
              sx={{ position: "absolute", top: 8, right: 8 }}
            >
              Retirer
            </Button>
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Please wait...</Typography>
          </Box>
        )}

        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress color="inherit" size={18} /> : <SendIcon />}
          onClick={handleSend}
          disabled={!canSend}
          fullWidth
        >
          {loading ? "Please wait..." : "Envoyer"}
        </Button>

      </Box>
    </CardSection>
  );
}
